import client from './inventory-request';
import { withStoreCreateBody } from '../hooks/useStoreScope';

function ss(key) {
  const v = sessionStorage.getItem(key);
  return v && v !== 'null' && v !== 'undefined' ? v : null;
}

function userId() {
  return ss('USER_ACCESS_KEY');
}

function grnActionBody(opts = {}, actorField) {
  const body = { ...opts };
  const actor = opts[actorField] || userId();
  if (actor) body[actorField] = actor;
  else delete body[actorField];
  return body;
}

// EMR resolves tenant_id/hospital_id from the Org-Code header for every request —
// the frontend must never send them in the body or query (per EMR inventory API contract).
function grnScopeParams() {
  return {};
}

function mapGrnItemCreateBody(grnId, body) {
  const {
    po_item_id,
    purchase_order_item_id,
    unit_rate,
    purchase_rate,
    ...rest
  } = body;
  const payload = { grn_id: grnId, ...rest };
  const poiId = purchase_order_item_id || po_item_id;
  if (poiId) payload.purchase_order_item_id = poiId;
  const rate = purchase_rate ?? unit_rate;
  if (rate !== undefined && rate !== null && rate !== '') {
    payload.purchase_rate = rate;
  }
  return payload;
}

// GRNs
// API: GET /inventory/grns/
export const getGrns   = (p) => client.get('/inventory/grns/', p);
// API: GET /inventory/grns/invoice-check/ — { available } only; never names the GRN holding the bill.
export const checkGrnInvoice = (p) => client.get('/inventory/grns/invoice-check/', p);
// API: GET /inventory/grns/{id}/
export const getGrn    = (id) => client.get(`/inventory/grns/${id}/`);
// API: POST /inventory/grns/
export const createGrn = (body) => client.post('/inventory/grns/', withStoreCreateBody(body));
// API: POST /inventory/grns/direct/ — atomic direct receipt (no purchase order)
export const createDirectGrn = (body) => client.post('/inventory/grns/direct/', withStoreCreateBody(body));
// API: PATCH /inventory/grns/{id}/
export const updateGrn = (id, body) => client.patch(`/inventory/grns/${id}/`, body);
// API: DELETE /inventory/grns/{id}/ — soft-delete; the backend sets status=CANCELLED and is_active=false,
// so this is the cancel action too. Precondition: status must be DRAFT or RECEIVED
// (QC_PENDING/QC_COMPLETED return 422, POSTED_TO_STOCK returns 409). Takes no body.
export const cancelGrn = (id) => client.delete(`/inventory/grns/${id}/`);

// GRN Items
// API: GET /inventory/grn-items/ — filter by grn_id
export const getGrnItems = (grnId) => client.get('/inventory/grn-items/', { grn_id: grnId });
// API: GET /inventory/grn-items/{id}/
export const getGrnItem    = (id) => client.get(`/inventory/grn-items/${id}/`);
// API: POST /inventory/grn-items/
export const createGrnItem = (body, scope = {}) =>
  client.post('/inventory/grn-items/', mapGrnItemCreateBody(body.grn_id, body), grnScopeParams(scope));
// API: PATCH /inventory/grn-items/{id}/
export const updateGrnItem = (id, body) => client.patch(`/inventory/grn-items/${id}/`, body);
// API: POST /inventory/grn-items/
export const addGrnItem = (grnId, body, scope = {}) =>
  client.post('/inventory/grn-items/', mapGrnItemCreateBody(grnId, body), grnScopeParams(scope));

// GRN Workflow — dedicated sub-resource actions (grn-batch-service)
// API: POST /inventory/grns/{grn_id}/mark-received
// Precondition: GRN status must be DRAFT (or already RECEIVED). Transitions to QC_PENDING.
export async function markReceived(grnId, opts = {}, scope = {}) {
  const body = grnActionBody(opts, 'received_by');
  return client.post(
    `/inventory/grns/${grnId}/mark-received/`,
    body,
    grnScopeParams(scope),
  );
}

// API: POST /inventory/qc/apply-qc/
// Body: { grn_item_id, qc_status, accepted_quantity, rejected_quantity, rejection_reason?, checked_by?, updated_by? }
// Precondition: sets the item's qc_status (PASSED/FAILED/PARTIALLY_PASSED/WAIVED). Must run for every
// active GRN item before /inventory/grns/{grn_id}/complete-qc/ will accept the GRN.
export const applyQc = (grnItemId, body = {}, scope = {}) => {
  const actor = body.updated_by || body.checked_by || userId();
  const payload = {
    grn_item_id: grnItemId,
    ...body,
  };
  if (actor) {
    if (!payload.updated_by) payload.updated_by = actor;
    if (!payload.checked_by) payload.checked_by = actor;
  }
  return client.post('/inventory/qc/apply-qc/', payload, grnScopeParams(scope));
};

// API: POST /inventory/grns/{grn_id}/complete-qc/
// Query: { updated_by }
// Response: { grn_id, qc_status, status, message }
export const completeQc = (grnId, opts = {}, scope = {}) => {
  const actor = opts.updated_by || userId();
  const params = grnScopeParams(scope);
  if (actor) params.updated_by = actor;
  return client.post(`/inventory/grns/${grnId}/complete-qc/`, {}, params);
};

// API: POST /inventory/grns/{grn_id}/post-to-stock/
// Precondition: GRN must be QC_COMPLETED with at least one item accepted_quantity > 0
// and batch_status = APPROVED. Transitions to POSTED_TO_STOCK.
export const postToStock = (grnId, opts = {}, scope = {}) => {
  const body = {};
  const actor = opts.created_by || opts.posted_by || userId();
  if (actor) body.created_by = actor;
  return client.post(`/inventory/grns/${grnId}/post-to-stock/`, body, grnScopeParams(scope));
};
export const postGrnToStock = postToStock;
